import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { CategoryNode } from '@biyolink/shared';
import { Skeleton } from '@/components/ui';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/cn';
import { t } from '@/i18n';

/**
 * Vitrinin sol kategori menüsü (müşteri notu: sahibinden benzeri).
 *
 * Ana kategoriler küçük ürün görseliyle, alt kategoriler ilan sayısıyla listelenir.
 * Sayılar sunucudan geliyor (`CategoryNode.listingCount`) — ana kategorinin sayısı
 * kendi ve alt kategorilerinin toplamı.
 *
 * Ana kategori açılıp kapanabiliyor: 26 ürünün hepsini birden açmak menüyü sayfadan
 * taşırıyor, kullanıcı ilanları görmek yerine kaydırmak zorunda kalıyordu.
 */
export function CategorySidebar({
  categories,
  isLoading,
  activeSlug,
}: {
  categories: CategoryNode[] | undefined;
  isLoading: boolean;
  activeSlug?: string;
}) {
  const [open, setOpen] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} className="h-12 rounded-xl" />
        ))}
      </div>
    );
  }

  // Kendi etiketi var: başlıktaki kategori mega-menüsü de "Kategoriler" diye
  // etiketliydi ve iki ayrı gezinme aynı adı taşıyınca ekran okuyucuda hangisinin
  // hangisi olduğu ayırt edilemiyordu.
  return (
    <nav aria-label={t('home.sidebarNavLabel')} className="text-sm">
      <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {t('home.sidebarTitle')}
      </p>

      <Link
        to="/ilanlar"
        className={cn(
          'mb-1 flex items-center justify-between rounded-xl px-3 py-2 font-medium transition',
          activeSlug ? 'text-slate-700 hover:bg-slate-100' : 'bg-primary-50 text-primary-900',
        )}
      >
        {t('home.sidebarAll')}
      </Link>

      <ul className="space-y-1">
        {categories?.map((type) => {
          const expanded = open === type.id || type.children.some((c) => c.slug === activeSlug);

          return (
            <li key={type.id}>
              <div className="flex items-stretch gap-1">
                <Link
                  to={`/kategori/${type.slug}`}
                  className={cn(
                    'flex min-w-0 flex-1 items-center gap-2 rounded-xl px-2 py-2 transition hover:bg-slate-100',
                    type.slug === activeSlug && 'bg-primary-50 text-primary-900',
                  )}
                >
                  <CategoryThumb imageUrl={firstImage(type)} />
                  <span className="min-w-0 flex-1 truncate font-medium text-slate-800">
                    {type.name}
                  </span>
                  <span className="shrink-0 text-xs tabular-nums text-slate-500">
                    {formatNumber(type.listingCount)}
                  </span>
                </Link>

                {type.children.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setOpen(expanded ? null : type.id)}
                    aria-expanded={expanded}
                    aria-label={type.name}
                    className="rounded-xl px-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                  >
                    <span
                      aria-hidden="true"
                      className={cn('block transition', expanded && 'rotate-90')}
                    >
                      ›
                    </span>
                  </button>
                )}
              </div>

              {expanded && (
                <ul className="mb-1 ml-4 space-y-0.5 border-l border-slate-200 pl-3">
                  {type.children.map((product) => (
                    <li key={product.id}>
                      <Link
                        to={`/kategori/${product.slug}`}
                        className={cn(
                          'flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-slate-600 transition hover:bg-slate-100 hover:text-primary-900',
                          product.slug === activeSlug &&
                            'bg-primary-50 font-medium text-primary-900',
                        )}
                      >
                        <span className="min-w-0 truncate">{product.name}</span>
                        <span className="shrink-0 text-xs tabular-nums text-slate-400">
                          {formatNumber(product.listingCount)}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/** Ana kategorinin görseli yok; ilk ürününün fotoğrafı temsil ediyor. */
function firstImage(type: CategoryNode): string | null {
  return type.imageUrl ?? type.children.find((child) => child.imageUrl)?.imageUrl ?? null;
}

/**
 * Kare küçük görsel.
 *
 * Kaynak fotoğraflar (biyolink.com/urunlerimiz) 208×208 DAİRESEL kesim, köşeleri
 * saydam. `object-cover` ile dikdörtgen çerçeveye oturtulunca köşeler zemin rengiyle
 * dolup görsel yuvarlak duruyordu. Kare çerçeve + `object-contain` + açık zemin,
 * eldeki fotoğrafla alınabilecek en düzgün sonuç. Gerçekten kare fotoğraf için
 * müşteriden yeni görsel gerekiyor.
 */
export function CategoryThumb({ imageUrl }: { imageUrl: string | null }) {
  return (
    <span className="grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-md bg-slate-100">
      {imageUrl ? (
        // `alt=""`: kategori adı bağlantının kendisinde zaten yazıyor. Buraya da ad
        // koymak ekran okuyucuda ismin iki kez okunmasına yol açıyordu.
        <img src={imageUrl} alt="" loading="lazy" className="h-full w-full object-contain" />
      ) : (
        <span aria-hidden="true" className="text-sm">
          ♻️
        </span>
      )}
    </span>
  );
}
